import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * Chip de ESTADO (pagada, en taller, a pedido, mora) o etiqueta neutra.
 * Para el codigo de moneda al lado de un numero esta `ChipMoneda` en `money/`;
 * un Badge con tinte de moneda se leeria como estado, por eso no existe.
 *
 * Es un <span>: va adentro de <p> y <td> en linea, y no recibe foco.
 *
 * La MAYUSCULA la pone la clase `uppercase`, no los strings de las etiquetas:
 * el texto real sigue siendo "contado", "en taller", y asi un lector de
 * pantalla lo lee como palabra en vez de deletrearlo. Ninguna etiqueta debe
 * escribirse ya en mayuscula a mano; la mayuscula vive solo aca.
 *
 * `tracking-wide` porque la mayuscula sin aire se empasta, igual que en el
 * thead de `Table` y en `TituloSeccion variante="etiqueta"`. Y `px-1.5` en vez
 * de `px-2`: la mayuscula ensancha la palabra ~30%, y estas etiquetas viven en
 * celdas angostas — lo que se gana de ancho en la letra se devuelve en el
 * relleno para que la etiqueta ocupe casi lo mismo que antes.
 *
 * `rounded-md` y no `rounded-full`: ninguna etiqueta de texto del sistema es
 * una pildora — lo redondo quedo solo para indicadores sin texto (barras de
 * progreso, puntos). Del tag de moneda, que ahora tambien es rectangular, lo
 * separan el radio mas grande, el cuerpo de letra (`text-xs` contra `text-2xs`)
 * y el relleno; ya no la forma.
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide [&_svg]:size-3 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground",
        // "muted" del MASTER seccion 10: entregado, discontinuado, anulado viejo.
        secondary: "bg-secondary text-secondary-foreground",
        // Etiqueta neutra: "Stock", "De baja".
        outline: "border border-border text-foreground",
        success: "bg-success/15 text-success",
        warning: "bg-warning/15 text-warning",
        destructive: "bg-destructive/15 text-destructive",
        info: "bg-info/15 text-info",
      },
    },
    defaultVariants: { variant: "secondary" },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps): JSX.Element {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { badgeVariants };
